import type { StyledComponent } from '@emotion/styled';
import { styled, type Theme } from '@mui/material/styles';
import { buttonBaseClasses } from '@mui/material/ButtonBase';
import { tabClasses } from '@mui/material/Tab';
import MuiTabs, { tabsClasses, type TabsProps } from '@mui/material/Tabs';

export const StyledTabs: StyledComponent<TabsProps> = styled(MuiTabs)(({ theme, variant }) => ({
  width: '100%',
  minHeight: 32,
  boxShadow: `inset 0 -1px 0 ${theme.palette.lwSecondaryAccentDark['10']}`,
  ...(variant === 'scrollable' ? {} : { overflow: 'visible' }),

  [`& .${tabsClasses.flexContainer}`]: {
    gap: theme.spacing(6),
    alignItems: 'center',
  },

  ...(variant === 'scrollable'
    ? {}
    : {
        [`& .${tabsClasses.scroller}`]: {
          overflow: 'visible',
          overflowX: 'visible',
          overflowY: 'visible',
        },
      }),

  [`&& .${tabsClasses.indicator}`]: {
    display: 'block',
    height: 2,
    backgroundColor: theme.palette.lwPrimaryAccentLight['100'],
    transition: 'none',
  },

  [`&:has(.${tabClasses.selected}.${buttonBaseClasses.focusVisible}) .${tabsClasses.indicator}`]: {
    backgroundColor: 'transparent',
  },

  [`&:has(.${tabClasses.selected}:focus-visible) .${tabsClasses.indicator}`]: {
    backgroundColor: 'transparent',
  },
}));

export const tabRootStyles = ({ theme }: { theme: Theme }) => ({
  [`&&.${tabClasses.root}`]: {
    minWidth: 0,
    minHeight: 32,
    height: 32,
    maxWidth: 'none',
    padding: theme.spacing(1, 0),
    overflow: 'visible',
    textTransform: 'none',
    letterSpacing: 0,
    borderRadius: 0,
    fontFamily: theme.typography.fontFamily,
    fontSize: '16px',
    fontWeight: 500,
    fontVariationSettings: '"wght" 500',
    lineHeight: '20px',
    color: theme.palette.lwSecondaryAccentDark['60'],
    backgroundColor: 'transparent',

    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 2,
      backgroundColor: 'transparent',
    },

    [`&.${tabClasses.selected}`]: {
      fontWeight: 500,
      fontVariationSettings: '"wght" 500',
      backgroundColor: 'transparent',
      color: theme.palette.lwPrimaryAccentLight['100'],
    },

    [`&:hover:not(.${tabClasses.selected}):not(.${tabClasses.disabled})`]: {
      backgroundColor: 'transparent',
      '&::after': {
        backgroundColor: theme.palette.lwPrimaryAccentLight['40'],
      },
    },

    [`&.${buttonBaseClasses.focusVisible}, &:focus-visible`]: {
      fontWeight: 500,
      fontVariationSettings: '"wght" 500',
      backgroundColor: 'transparent',
      color: theme.palette.lwPrimaryAccentLight['100'],
      borderRadius: 8,
      padding: theme.spacing(1, 0),
      boxShadow: `inset 0 0 0 1.5px ${theme.palette.lwPrimaryAccentLight['100']}`,
      '&::after': {
        backgroundColor: 'transparent',
      },
    },

    [`&.${tabClasses.disabled}`]: {
      opacity: 1,
      backgroundColor: 'transparent',
      color: theme.palette.lwSecondaryAccentDark['40'],
      boxShadow: 'none',
    },
  },
});
